import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ShoppingCart, Plus, Minus, X, CreditCard, Banknote, Receipt, Calendar, Search, User, CalendarPlus, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

export default function POSPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [cart, setCart] = useState([]);
  const [tip, setTip] = useState(0);
  const [tipSplits, setTipSplits] = useState({}); // { provider_id: amount }
  const [tipSplitMode, setTipSplitMode] = useState("auto"); // "auto" | "manual"
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("services");
  const [linkedAppointment, setLinkedAppointment] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientCards, setClientCards] = useState([]);
  // Rebook dialog: opens after a successful checkout when the cart had at
  // least one service. Captures a client snapshot before we wipe cart state
  // so the dialog can default to the same client/provider/services.
  const [rebookOpen, setRebookOpen] = useState(false);
  const [rebookSnapshot, setRebookSnapshot] = useState(null);
  const [rebookWeeks, setRebookWeeks] = useState(6);
  const [rebookSlots, setRebookSlots] = useState([]);
  const [rebookLoadingSlots, setRebookLoadingSlots] = useState(false);
  const [rebookSubmitting, setRebookSubmitting] = useState(false);
  const [rebookSelectedSlot, setRebookSelectedSlot] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [staff, setStaff] = useState([]);
  const clientRef = useRef(null);

  // Filtered items based on search
  const filteredItems = useMemo(() => {
    const items = activeTab === "services" ? services : products;
    if (!itemSearch.trim()) return items;
    const q = itemSearch.toLowerCase();
    return items.filter(item =>
      item.name?.toLowerCase().includes(q) ||
      item.category?.toLowerCase().includes(q) ||
      item.sku?.toLowerCase().includes(q)
    );
  }, [activeTab, services, products, itemSearch]);

  // Filtered clients based on search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients.slice(0, 20);
    const q = clientSearch.toLowerCase();
    return clients.filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.includes(q)
    ).slice(0, 20);
  }, [clients, clientSearch]);

  // Get selected client display name
  const selectedClientName = useMemo(() => {
    if (!selectedClient) return "";
    const c = clients.find(cl => cl.client_id === selectedClient);
    return c ? `${c.first_name} ${c.last_name}` : "";
  }, [selectedClient, clients]);

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Load cards when client selected
  useEffect(() => {
    if (selectedClient) {
      api.get(`/clients/${selectedClient}/cards`).then(res => {
        setClientCards(res.data);
        const defaultCard = res.data.find(c => c.is_default);
        if (defaultCard) {
          setSelectedCard(defaultCard);
          setPaymentMethod("card_on_file");
        } else {
          setSelectedCard(null);
        }
      }).catch(() => setClientCards([]));
    } else {
      setClientCards([]);
      setSelectedCard(null);
      if (paymentMethod === "card_on_file") setPaymentMethod("card");
    }
  }, [selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load any unbilled Color Bar charges for the selected client so the color
  // service never slips through checkout.
  const [colorCharges, setColorCharges] = useState([]);
  useEffect(() => {
    if (!selectedClient) { setColorCharges([]); return; }
    api.get(`/color-bar/unbilled?client_id=${selectedClient}`)
      .then(res => setColorCharges(res.data || []))
      .catch(() => setColorCharges([]));
  }, [selectedClient]);

  const addColorCharge = (f) => {
    const id = `color_${f.formula_id}`;
    if (cart.find(c => c.id === id)) return;
    setCart([...cart, {
      id, formula_id: f.formula_id,
      name: `Color service${f.stylist_name ? ` — ${f.stylist_name}` : ""}`,
      price: f.suggested_charge, quantity: 1, type: "service",
      provider_id: f.stylist_id || "", provider_name: f.stylist_name || "",
    }]);
    setColorCharges(prev => prev.filter(c => c.formula_id !== f.formula_id));
    toast.success("Color charge added to ticket");
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [svcRes, prodRes, cliRes, locRes, staffRes] = await Promise.all([
          api.get("/services"),
          api.get("/products"),
          api.get("/clients?limit=200"),
          api.get("/locations"),
          api.get("/staff"),
        ]);
        setServices(svcRes.data);
        setProducts(prodRes.data);
        setClients(cliRes.data.clients || []);
        setLocations(locRes.data);
        setStaff(staffRes.data.filter(s => ["stylist", "esthetician", "nail_tech", "colorist"].includes(s.role)));
        if (locRes.data.length > 0) setSelectedLocation(locRes.data[0].location_id);

        // Pre-fill from URL params (from calendar checkout)
        const apptId = searchParams.get("appointment_id");
        const clientId = searchParams.get("client_id");
        const locationId = searchParams.get("location_id");
        const serviceIds = searchParams.get("service_ids");
        const serviceNamesParam = searchParams.get("service_names");
        const totalPrice = searchParams.get("total_price");
        const providerName = searchParams.get("provider_name");

        if (apptId && clientId) {
          setLinkedAppointment({ appointment_id: apptId, provider_name: providerName, client_name: searchParams.get("client_name") });
          setSelectedClient(clientId);
          if (locationId) setSelectedLocation(locationId);

          // Build cart from appointment services
          if (serviceIds) {
            const ids = serviceIds.split(",").filter(Boolean);
            const names = serviceNamesParam ? serviceNamesParam.split(",") : [];
            const apptProviderId = searchParams.get("provider_id") || "";
            const apptProviderName = providerName || "";
            const cartItems = ids.map((sid, i) => {
              const svc = svcRes.data.find(s => s.service_id === sid);
              return {
                id: sid,
                name: svc?.name || names[i] || "Service",
                price: svc?.price || 0,
                quantity: 1,
                type: "service",
                provider_id: apptProviderId,
                provider_name: apptProviderName,
              };
            });
            setCart(cartItems);
          }
          // Clear search params so refresh doesn't re-load
          setSearchParams({}, { replace: true });
          toast.success(`Loaded appointment for ${searchParams.get("client_name") || "client"}`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = (item, type) => {
    const existing = cart.find(c => c.id === item[type === "service" ? "service_id" : "product_id"]);
    if (existing) {
      setCart(cart.map(c => c.id === existing.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, {
        id: item[type === "service" ? "service_id" : "product_id"],
        name: item.name,
        price: item.price,
        quantity: 1,
        type,
        provider_id: type === "service" ? (staff[0]?.user_id || "") : "",
        provider_name: type === "service" ? (staff[0]?.name || "") : "",
      }]);
    }
  };

  const updateItemProvider = (itemId, providerId) => {
    const provider = staff.find(s => s.user_id === providerId);
    setCart(cart.map(c => c.id === itemId ? { ...c, provider_id: providerId, provider_name: provider?.name || "" } : c));
  };

  const updateQuantity = (id, delta) => {
    setCart(cart.map(c => {
      if (c.id === id) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : c;
      }
      return c;
    }).filter(c => c.quantity > 0));
  };

  const removeItem = (id) => {
    setCart(cart.filter(c => c.id !== id));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const taxRate = 0.08;
  const tax = subtotal * taxRate;
  const total = subtotal + tax + tip - discount;

  // Providers present in the current cart (unique, ordered by first appearance)
  const cartProviders = useMemo(() => {
    const seen = new Map();
    for (const item of cart) {
      if (item.type === "service" && item.provider_id && !seen.has(item.provider_id)) {
        seen.set(item.provider_id, {
          provider_id: item.provider_id,
          provider_name: item.provider_name || (staff.find(s => s.user_id === item.provider_id)?.name || ""),
        });
      }
    }
    return Array.from(seen.values());
  }, [cart, staff]);

  // Auto-distribute tip equally among cart providers whenever tip or provider list changes in "auto" mode
  useEffect(() => {
    if (tipSplitMode !== "auto") return;
    if (tip <= 0 || cartProviders.length < 2) {
      setTipSplits({});
      return;
    }
    const share = Math.floor((tip / cartProviders.length) * 100) / 100;
    const next = {};
    let running = 0;
    cartProviders.forEach((p, i) => {
      const amt = i === cartProviders.length - 1
        ? Math.round((tip - running) * 100) / 100
        : share;
      next[p.provider_id] = amt;
      running += amt;
    });
    setTipSplits(next);
  }, [tip, cartProviders, tipSplitMode]);

  const tipSplitSum = Object.values(tipSplits).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const tipSplitValid = cartProviders.length < 2 || Math.abs(tipSplitSum - tip) < 0.01;

  const updateSplit = (provider_id, raw) => {
    const n = parseFloat(raw);
    setTipSplits(prev => ({ ...prev, [provider_id]: Number.isFinite(n) ? n : 0 }));
    setTipSplitMode("manual");
  };

  const checkout = async () => {
    if (!selectedClient || cart.length === 0) {
      toast.error("Please select a client and add items to cart");
      return;
    }

    // Card payment requires a saved card
    if ((paymentMethod === "card" || paymentMethod === "card_on_file") && !selectedCard) {
      toast.error("Please select a card on file or add one to the client profile first");
      return;
    }

    setProcessing(true);
    try {
      // Charge the card via Stripe if card payment selected
      if ((paymentMethod === "card" || paymentMethod === "card_on_file") && selectedCard) {
        const chargeRes = await api.post(`/clients/${selectedClient}/cards/${selectedCard.card_id}/charge`, { amount: total });
        if (chargeRes.data.status !== "succeeded") {
          toast.error(chargeRes.data.error || "Card charge failed");
          setProcessing(false);
          return;
        }
        toast.success(`Charged ${chargeRes.data.brand} ****${chargeRes.data.last4} — $${total.toFixed(2)}`);
      }

      // Build tip_splits payload only when applicable
      const tipSplitsPayload = tip > 0 && cartProviders.length >= 2
        ? cartProviders.map(p => ({
            provider_id: p.provider_id,
            provider_name: p.provider_name,
            amount: Math.round((parseFloat(tipSplits[p.provider_id]) || 0) * 100) / 100,
          }))
        : null;

      // Reject if splits don't match tip
      if (tipSplitsPayload) {
        const sum = tipSplitsPayload.reduce((s, x) => s + x.amount, 0);
        if (Math.abs(sum - tip) > 0.01) {
          toast.error(`Tip splits total $${sum.toFixed(2)} — must equal $${tip.toFixed(2)}`);
          setProcessing(false);
          return;
        }
      }

      // Create the salon transaction
      await api.post("/transactions", {
        appointment_id: linkedAppointment?.appointment_id || "",
        client_id: selectedClient,
        location_id: selectedLocation,
        items: cart.map(c => ({ type: c.type, name: c.name, price: c.price, quantity: c.quantity, provider_id: c.provider_id || "", provider_name: c.provider_name || "" })),
        subtotal: Math.round(subtotal * 100) / 100,
        tax: Math.round(tax * 100) / 100,
        discount: Math.round(discount * 100) / 100,
        tip: Math.round(tip * 100) / 100,
        tip_splits: tipSplitsPayload,
        total: Math.round(total * 100) / 100,
        payment_method: selectedCard ? `card (${selectedCard.brand} ****${selectedCard.last4})` : paymentMethod,
      });

      if (linkedAppointment?.appointment_id) {
        try { await api.put(`/appointments/${linkedAppointment.appointment_id}`, { status: "completed" }); } catch {}
      }
      toast.success("Transaction completed!");
      // Mark any Color Bar charges on this ticket as billed so they don't
      // resurface at the next checkout.
      for (const c of cart.filter(x => x.formula_id)) {
        try { await api.post(`/color-formulas/${c.formula_id}/mark-billed`); } catch {}
      }
      // Capture a snapshot for the rebook dialog before we reset cart state.
      // We rebook only when there was at least one *service* in the cart —
      // products alone don't justify a follow-up appointment.
      const serviceItems = cart.filter(c => c.type === "service");
      if (selectedClient && serviceItems.length > 0) {
        const clientObj = clients.find(c => c.client_id === selectedClient);
        // Pick the primary provider: first provider on a service line, falling back
        // to the linked appointment's provider, then to '' (will prompt user).
        const primaryProvider = serviceItems[0].provider_id || linkedAppointment?.provider_id || "";
        setRebookSnapshot({
          client_id: selectedClient,
          client_name: clientObj ? `${clientObj.first_name} ${clientObj.last_name}` : "this client",
          location_id: selectedLocation,
          provider_id: primaryProvider,
          service_ids: serviceItems.map(s => s.service_id || s.id).filter(Boolean),
          service_names: serviceItems.map(s => s.name),
          duration_minutes: serviceItems.reduce((sum, s) => sum + (Number(s.duration) || 60), 0),
        });
        setRebookWeeks(6);
        setRebookSelectedSlot(null);
        setRebookOpen(true);
      }
      setCart([]); setTip(0); setDiscount(0); setSelectedClient(""); setLinkedAppointment(null);
      setTipSplits({}); setTipSplitMode("auto");
    } catch (err) {
      toast.error("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  // Compute a date string `weeks` from today (YYYY-MM-DD).
  const computeRebookDate = (weeks) => {
    const d = new Date();
    d.setDate(d.getDate() + weeks * 7);
    return d.toISOString().split("T")[0];
  };

  // Pull available slots for the snapshot's primary service+provider on the
  // chosen target date. Uses /public/available-slots which already honors
  // per-staff custom_duration_minutes and existing bookings.
  const fetchRebookSlots = async (snapshot, weeks) => {
    if (!snapshot?.service_ids?.[0] || !snapshot?.location_id) return;
    setRebookLoadingSlots(true);
    setRebookSlots([]);
    setRebookSelectedSlot(null);
    try {
      const date = computeRebookDate(weeks);
      const params = new URLSearchParams({
        location_id: snapshot.location_id,
        service_id: snapshot.service_ids[0],
        date,
      });
      if (snapshot.provider_id) params.set("provider_id", snapshot.provider_id);
      const res = await api.get(`/public/available-slots?${params}`);
      setRebookSlots(res.data?.slots || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Rebook slot fetch failed", err);
      toast.error("Couldn't load slots — try a different week");
      setRebookSlots([]);
    } finally {
      setRebookLoadingSlots(false);
    }
  };

  // Whenever the dialog opens or the user changes the week offset, refetch.
  useEffect(() => {
    if (rebookOpen && rebookSnapshot) {
      fetchRebookSlots(rebookSnapshot, rebookWeeks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebookOpen, rebookWeeks, rebookSnapshot?.service_ids?.[0]]);

  const submitRebook = async () => {
    if (!rebookSelectedSlot || !rebookSnapshot) return;
    setRebookSubmitting(true);
    try {
      const date = computeRebookDate(rebookWeeks);
      const start = new Date(`${date}T${rebookSelectedSlot.time}:00`);
      const dur = Number(rebookSelectedSlot.duration) || rebookSnapshot.duration_minutes || 60;
      const end = new Date(start.getTime() + dur * 60000);
      await api.post("/appointments", {
        client_id: rebookSnapshot.client_id,
        provider_id: rebookSelectedSlot.provider_id || rebookSnapshot.provider_id,
        location_id: rebookSnapshot.location_id,
        service_ids: rebookSnapshot.service_ids,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        status: "booked",
        notes: "Rebooked at checkout",
      });
      toast.success(`${rebookSnapshot.client_name}'s next appointment booked for ${date} at ${rebookSelectedSlot.time}`);
      setRebookOpen(false);
      setRebookSnapshot(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Rebook failed", { status: err.response?.status, data: err.response?.data });
      toast.error(err.response?.data?.detail || "Couldn't book the appointment. Try a different time.");
    } finally {
      setRebookSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex" data-testid="pos-page">
      {/* Left - Items */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* POS Header */}
        <div className="sticky top-0 z-10 bg-white/70 backdrop-blur-xl border-b border-black/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="font-['Outfit'] text-2xl font-medium tracking-tight text-slate-900">Checkout</h1>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="pos-location-select" className="w-[160px] h-9 border-slate-200 bg-white rounded-lg text-sm">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Linked Appointment Banner */}
          {linkedAppointment && (
            <div className="mt-3 flex items-center gap-3 bg-[#E8F0E9] rounded-lg px-4 py-2.5" data-testid="linked-appointment-banner">
              <Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-600">
                  Checking out appointment for {linkedAppointment.client_name}
                </p>
                {linkedAppointment.provider_name && (
                  <p className="text-xs text-emerald-600/70">Provider: {linkedAppointment.provider_name}</p>
                )}
              </div>
              <button onClick={() => setLinkedAppointment(null)} className="text-emerald-600/50 hover:text-emerald-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Tabs + Search */}
          <div className="flex items-center gap-3 mt-4">
            <div className="flex gap-1 bg-[#FAFAFA] rounded-lg p-1 border border-slate-200">
              {["services", "products"].map(tab => (
                <button
                  key={tab}
                  data-testid={`pos-tab-${tab}`}
                  onClick={() => { setActiveTab(tab); setItemSearch(""); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize ${
                    activeTab === tab ? "bg-white shadow-sm text-slate-900" : "text-slate-600"
                  }`}
                >{tab}</button>
              ))}
            </div>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="pos-item-search"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder={`Search ${activeTab}...`}
                className="pl-9 h-9 border-slate-200 bg-white rounded-lg text-sm"
              />
              {itemSearch && (
                <button onClick={() => setItemSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Items Grid */}
        <div className="flex-1 overflow-auto p-6">
          {filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="w-10 h-10 text-[#E6E4E0] mb-3" strokeWidth={1} />
              <p className="text-sm text-slate-400">No {activeTab} found</p>
              {itemSearch && <p className="text-xs text-rose-400 mt-1">Try a different search term</p>}
            </div>
          ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredItems.map(item => (
              <button
                key={item.service_id || item.product_id}
                data-testid={`pos-item-${item.service_id || item.product_id}`}
                onClick={() => addToCart(item, activeTab === "services" ? "service" : "product")}
                className="bg-white border border-slate-200 rounded-2xl p-4 text-left hover:shadow-md hover:border-rose-300 transition-all duration-200 group"
              >
                {item.color && (
                  <div className="w-3 h-3 rounded-full mb-2" style={{ backgroundColor: item.color }} />
                )}
                <p className="text-sm font-medium text-slate-900 group-hover:text-slate-900">{item.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{item.category}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-base font-medium text-slate-900">${item.price}</span>
                  {item.duration && <span className="text-xs text-rose-400">{item.duration}min</span>}
                </div>
              </button>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Right - Cart */}
      <div className="w-[380px] bg-white border-l border-slate-200 flex flex-col" data-testid="pos-cart">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            <h2 className="font-['Outfit'] text-lg font-medium text-slate-900">Cart</h2>
            <span className="ml-auto text-sm text-slate-400">{cart.length} items</span>
          </div>
          {/* Client Search */}
          <div className="relative" ref={clientRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                data-testid="pos-client-search"
                type="text"
                value={showClientDropdown ? clientSearch : selectedClientName || clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                onFocus={() => setShowClientDropdown(true)}
                placeholder="Search client by name, email, or phone..."
                className="w-full h-10 pl-9 pr-8 border border-slate-200 bg-[#FAFAFA] rounded-lg text-sm outline-none focus:border-rose-300 focus:ring-1 focus:ring-[#B5A493] transition-colors"
              />
              {selectedClient && (
                <button
                  data-testid="clear-client-btn"
                  onClick={() => { setSelectedClient(""); setClientSearch(""); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {showClientDropdown && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[240px] overflow-y-auto" data-testid="client-dropdown">
                {filteredClients.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-slate-400">No clients found</div>
                ) : (
                  filteredClients.map(c => (
                    <button
                      key={c.client_id}
                      data-testid={`client-option-${c.client_id}`}
                      onClick={() => {
                        setSelectedClient(c.client_id);
                        setClientSearch("");
                        setShowClientDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-[#FAFAFA] transition-colors flex items-center justify-between ${
                        selectedClient === c.client_id ? 'bg-[#FAFAFA]' : ''
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-slate-400">{c.email || c.phone || ""}</p>
                      </div>
                      {selectedClient === c.client_id && (
                        <div className="w-2 h-2 rounded-full bg-emerald-600" />
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-auto p-4 space-y-2">
          {colorCharges.length > 0 && (
            <div className="space-y-2" data-testid="pos-color-charges">
              {colorCharges.map(f => (
                <div key={f.formula_id} data-testid={`pos-color-charge-${f.formula_id}`}
                  className="p-3 rounded-lg bg-gradient-to-r from-fuchsia-50 to-violet-50 border border-fuchsia-200 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-fuchsia-900">Color Bar charge · ${Number(f.suggested_charge).toFixed(2)}</p>
                    <p className="text-xs text-fuchsia-700/70">{f.stylist_name || "Colorist"} · {f.service_date} · ${Number(f.total_cost).toFixed(2)} product cost</p>
                  </div>
                  <button onClick={() => addColorCharge(f)} data-testid={`pos-add-color-${f.formula_id}`}
                    className="shrink-0 h-9 px-4 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-semibold flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
              ))}
            </div>
          )}
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Receipt className="w-10 h-10 text-[#E6E4E0] mb-3" strokeWidth={1} />
              <p className="text-sm text-slate-400">Cart is empty</p>
              <p className="text-xs text-rose-400 mt-1">Add services or products</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="p-3 bg-[#FAFAFA] rounded-lg" data-testid={`cart-item-${item.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                    <p className="text-xs text-slate-400">${item.price} x {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQuantity(item.id, -1)} className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center hover:bg-[#FAFAFA]">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-sm">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)} className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center hover:bg-[#FAFAFA]">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm font-medium text-slate-900 w-14 text-right">${(item.price * item.quantity).toFixed(2)}</p>
                  <button onClick={() => removeItem(item.id)} className="text-slate-400 hover:text-rose-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {/* Stylist selector for services */}
                {item.type === "service" && (
                  <div className="mt-2 flex items-center gap-2">
                    <User className="w-3 h-3 text-slate-400" />
                    <select
                      data-testid={`provider-select-${item.id}`}
                      value={item.provider_id || ""}
                      onChange={e => updateItemProvider(item.id, e.target.value)}
                      className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-rose-300 text-slate-900"
                    >
                      <option value="">Select stylist...</option>
                      {staff.map(s => (
                        <option key={s.user_id} value={s.user_id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Summary & Checkout */}
        <div className="border-t border-slate-200 p-6 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Subtotal</span>
            <span className="text-slate-900 font-medium">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Tax (8%)</span>
            <span className="text-slate-900">${tax.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Tip</span>
            <div className="flex gap-1">
              {[0, 5, 10, 15, 20].map(t => (
                <button
                  key={t}
                  data-testid={`tip-${t}`}
                  onClick={() => setTip(t === 0 ? 0 : (subtotal * t / 100))}
                  className={`px-2 py-0.5 rounded text-xs transition-colors ${
                    Math.round(tip) === Math.round(subtotal * t / 100) && t > 0
                      ? "bg-slate-900 text-white" : "bg-[#FAFAFA] text-slate-600 hover:bg-rose-50"
                  }`}
                >{t === 0 ? 'No' : `${t}%`}</button>
              ))}
            </div>
          </div>
          {tip > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Tip amount</span>
              <span className="text-slate-900">${tip.toFixed(2)}</span>
            </div>
          )}

          {/* Tip split (only when 2+ providers serviced the ticket) */}
          {tip > 0 && cartProviders.length >= 2 && (
            <div className="rounded-lg border border-slate-200 bg-[#FAFAFA] p-3" data-testid="tip-split-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700">Split tip</span>
                <div className="flex gap-1">
                  <button
                    data-testid="tip-split-auto"
                    onClick={() => setTipSplitMode("auto")}
                    className={`text-[10px] px-2 py-0.5 rounded ${tipSplitMode === "auto" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                    Equal
                  </button>
                  <button
                    data-testid="tip-split-manual"
                    onClick={() => setTipSplitMode("manual")}
                    className={`text-[10px] px-2 py-0.5 rounded ${tipSplitMode === "manual" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                    Manual
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                {cartProviders.map(p => (
                  <div key={p.provider_id} className="flex items-center gap-2" data-testid={`tip-split-row-${p.provider_id}`}>
                    <span className="flex-1 text-xs text-slate-700 truncate">{p.provider_name}</span>
                    <span className="text-xs text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      disabled={tipSplitMode === "auto"}
                      value={tipSplits[p.provider_id] ?? 0}
                      onChange={e => updateSplit(p.provider_id, e.target.value)}
                      data-testid={`tip-split-input-${p.provider_id}`}
                      className="w-20 h-7 border border-slate-200 rounded text-xs px-2 text-right bg-white disabled:bg-[#FAFAFA] disabled:text-slate-500 outline-none focus:border-rose-300"
                    />
                    <span className="text-[10px] text-slate-400 w-8 text-right">
                      {tip > 0 ? `${Math.round(((parseFloat(tipSplits[p.provider_id]) || 0) / tip) * 100)}%` : ""}
                    </span>
                  </div>
                ))}
              </div>
              <div className={`mt-2 text-[11px] flex justify-between ${tipSplitValid ? "text-slate-400" : "text-rose-600 font-medium"}`}>
                <span>Sum</span>
                <span>${tipSplitSum.toFixed(2)} / ${tip.toFixed(2)}</span>
              </div>
              {!tipSplitValid && (
                <p className="text-[10px] text-rose-600 mt-1" data-testid="tip-split-error">
                  Splits must add up to the full tip.
                </p>
              )}
            </div>
          )}

          <div className="border-t border-slate-200 pt-3 flex justify-between">
            <span className="font-['Outfit'] text-lg font-medium text-slate-900">Total</span>
            <span className="font-['Outfit'] text-lg font-medium text-slate-900">${total.toFixed(2)}</span>
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <button
                data-testid="pay-card"
                onClick={() => { setPaymentMethod("card"); if (clientCards.length > 0 && !selectedCard) setSelectedCard(clientCards.find(c => c.is_default) || clientCards[0]); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  paymentMethod === "card" || paymentMethod === "card_on_file" ? "bg-slate-900 text-white" : "bg-[#FAFAFA] text-slate-600 border border-slate-200"
                }`}
              >
                <CreditCard className="w-4 h-4" /> Card
              </button>
              <button
                data-testid="pay-cash"
                onClick={() => { setPaymentMethod("cash"); setSelectedCard(null); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  paymentMethod === "cash" ? "bg-slate-900 text-white" : "bg-[#FAFAFA] text-slate-600 border border-slate-200"
                }`}
              >
                <Banknote className="w-4 h-4" /> Cash
              </button>
            </div>
            {(paymentMethod === "card" || paymentMethod === "card_on_file") && clientCards.length > 0 && (
              <div className="bg-[#E8F0E9] rounded-lg px-3 py-2" data-testid="card-on-file-info">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-600">
                      {selectedCard?.brand} ****{selectedCard?.last4}
                    </span>
                  </div>
                  {clientCards.length > 1 && (
                    <select
                      data-testid="select-card-on-file"
                      value={selectedCard?.card_id || ""}
                      onChange={(e) => setSelectedCard(clientCards.find(c => c.card_id === e.target.value))}
                      className="text-xs border border-[#2D5936]/30 rounded-md px-2 py-1 bg-white text-emerald-600 outline-none"
                    >
                      {clientCards.map(c => (
                        <option key={c.card_id} value={c.card_id}>{c.brand} ****{c.last4}{c.is_default ? " (default)" : ""}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}
            {(paymentMethod === "card" || paymentMethod === "card_on_file") && clientCards.length === 0 && selectedClient && (
              <div className="bg-[#FEF3CD] rounded-lg px-3 py-2 text-xs text-[#856404]">
                No card on file. Add one from the client's profile to charge via Stripe.
              </div>
            )}
          </div>

          <Button
            data-testid="checkout-btn"
            onClick={checkout}
            disabled={processing || cart.length === 0 || !tipSplitValid}
            className="w-full h-12 bg-slate-900 text-white hover:bg-slate-800 rounded-full text-base font-medium"
          >
            {processing ? "Processing..." : selectedCard ? `Charge ${selectedCard.brand} ****${selectedCard.last4} — $${total.toFixed(2)}` : `Charge $${total.toFixed(2)}`}
          </Button>
        </div>
      </div>

      {/* Rebook-after-checkout dialog */}
      <Dialog open={rebookOpen} onOpenChange={(o) => { setRebookOpen(o); if (!o) setRebookSnapshot(null); }}>
        <DialogContent className="max-w-lg" data-testid="rebook-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
                <CalendarPlus className="w-5 h-5 text-rose-600" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <DialogTitle className="font-['Outfit'] text-xl tracking-tight">
                  Book {rebookSnapshot?.client_name?.split(" ")[0] || "this client"}'s next visit?
                </DialogTitle>
                <DialogDescription className="text-xs font-['Manrope'] mt-1 leading-relaxed">
                  Lock in a follow-up before they walk out — clients rebooked at checkout return ~3× more often than ones who don't.
                  {rebookSnapshot?.service_names?.length ? <>
                    <br /><span className="text-slate-700 font-medium">Same service{rebookSnapshot.service_names.length > 1 ? "s" : ""}:</span> {rebookSnapshot.service_names.join(", ")}
                  </> : null}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Week-offset chips */}
          <div data-testid="rebook-weeks-row">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope'] mb-2">When?</p>
            <div className="flex flex-wrap gap-2">
              {[3, 4, 5, 6, 8, 10, 12].map(w => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setRebookWeeks(w)}
                  data-testid={`rebook-weeks-${w}`}
                  className={`px-3 h-8 rounded-full text-[12px] font-semibold font-['Manrope'] transition
                    ${rebookWeeks === w
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}
                >
                  In {w} wk{w !== 1 ? "s" : ""}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5 font-['Manrope']" data-testid="rebook-target-date">
              Target date: <span className="text-slate-700 font-medium tabular-nums">{computeRebookDate(rebookWeeks)}</span>
            </p>
          </div>

          {/* Slot grid */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Pick a time</p>
            {rebookLoadingSlots ? (
              <div data-testid="rebook-slots-loading" className="text-center py-4 text-sm text-slate-400 font-['Manrope']">
                Loading available slots…
              </div>
            ) : rebookSlots.length === 0 ? (
              <div data-testid="rebook-slots-empty" className="text-center py-4 px-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700 font-['Manrope']">
                No openings on {computeRebookDate(rebookWeeks)}. Try a different week, or rebook later from the calendar.
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto" data-testid="rebook-slots-grid">
                {rebookSlots.map((s, i) => {
                  const key = `${s.provider_id}_${s.time}`;
                  const isSelected = rebookSelectedSlot && rebookSelectedSlot.provider_id === s.provider_id && rebookSelectedSlot.time === s.time;
                  return (
                    <button
                      key={`${key}_${i}`}
                      type="button"
                      onClick={() => setRebookSelectedSlot(s)}
                      data-testid={`rebook-slot-${i}`}
                      className={`px-2 py-2 rounded-lg text-[12px] font-semibold tabular-nums transition border font-['Manrope']
                        ${isSelected
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                    >
                      {s.time}
                      {s.provider_name && rebookSlots.some(x => x.provider_id !== s.provider_id) && (
                        <div className="text-[9px] font-normal opacity-70 mt-0.5">{s.provider_name?.split(" ")[0]}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRebookOpen(false)} className="rounded-full" data-testid="rebook-skip-btn">
              Skip — they'll book later
            </Button>
            <Button
              onClick={submitRebook}
              disabled={!rebookSelectedSlot || rebookSubmitting}
              data-testid="rebook-confirm-btn"
              className="bg-rose-600 text-white rounded-full hover:bg-rose-700 disabled:opacity-50">
              {rebookSubmitting ? "Booking…" : (
                <span className="flex items-center gap-1.5">
                  Confirm rebook <ChevronRight className="w-3.5 h-3.5" />
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
